import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { useApp } from "../context/AppContext";
import { assistantApi } from "../services/api";

const EMPTY = {
  title: "",
  company: "",
  location: "",
  workType: "Remote",
  employmentType: "Full-time",
  salary: "",
  url: "",
  source: "Manual",
  description: "",
  requirements: [],
};

const DETECT_STEPS = ["Opening the job page", "Reading the posting", "Pulling out title, company and requirements"];

function AddJobModal() {
  const { addJob, closeAddJob, createJob, jobs, toast, integrations } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState("paste");
  const [url, setUrl] = useState("");
  const [pasted, setPasted] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detectIndex, setDetectIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const duplicate = form.url && jobs.find((j) => j.url === form.url);

  const withProgress = useCallback(async (work) => {
    setError("");
    setNotice(null);
    setStep("detecting");
    setDetectIndex(0);
    const timer = setInterval(() => setDetectIndex((i) => Math.min(i + 1, DETECT_STEPS.length - 1)), 1800);
    try {
      return await work();
    } finally {
      clearInterval(timer);
    }
  }, []);

  const detect = useCallback(
    async (link) => {
      if (!link.trim()) return;
      const cleanUrl = link.trim();
      if (!integrations.ai) {
        try {
          const page = await withProgress(() => assistantApi.readPage(cleanUrl));
          setForm({ ...EMPTY, url: page.url, source: page.source, description: page.text });
          setNotice({ variant: "warning", text: "We copied the page text. Fill in the title and company. (Reading job details automatically uses AI, which is available by request.)" });
        } catch (err) {
          setForm({ ...EMPTY, url: cleanUrl });
          setError(err.message);
        }
        setStep("review");
        return;
      }
      try {
        const { job } = await withProgress(() => assistantApi.parseJob({ url: cleanUrl }));
        setForm({ ...EMPTY, ...job });
        setNotice({ variant: "success", text: `Details detected from ${job.source}. Check them and fix anything that looks off.` });
        setStep("review");
      } catch (err) {
        setError(err.message);
        setStep(err.status === 422 ? "text" : "paste");
      }
    },
    [integrations.ai, withProgress]
  );

  const detectFromText = async () => {
    try {
      const { job } = await withProgress(() => assistantApi.parseJob({ url: url.trim(), text: pasted }));
      setForm({ ...EMPTY, ...job, source: job.source === "Manual" && url.trim() ? "Company site" : job.source });
      setNotice({ variant: "success", text: "Details pulled from the description you pasted. Check them before saving." });
      setStep("review");
    } catch (err) {
      setError(err.message);
      setStep("text");
    }
  };

  useEffect(() => {
    if (!addJob.open) return;
    setForm(EMPTY);
    setError("");
    setNotice(null);
    setPasted("");
    setUrl(addJob.url);
    if (addJob.url) detect(addJob.url);
    else setStep("paste");
    // Only re-run when the modal opens, not when `detect` changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addJob.open, addJob.url]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (startApplying) => {
    if (!form.title.trim() || !form.company.trim()) {
      setError("Add at least a job title and company.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const id = await createJob({ ...form, requirements: form.requirements.map((s) => s.trim()).filter(Boolean) });
      closeAddJob();
      toast(`${form.title} at ${form.company} added to Found`);
      navigate(startApplying ? `/jobs/${id}?tab=apply` : `/jobs/${id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const enterManually = () => {
    setForm({ ...EMPTY, url: url.trim() });
    setError("");
    setNotice(null);
    setStep("review");
  };

  const footer =
    step === "review" ? (
      <>
        <button className="btn btn-link text-muted me-auto px-0" onClick={() => setStep("paste")}>
          <i className="bi bi-arrow-left" /> Back
        </button>
        <button className="btn btn-outline-secondary" onClick={() => save(false)} disabled={saving}>
          Save for later
        </button>
        <button className="btn btn-primary" onClick={() => save(true)} disabled={saving}>
          {saving && <span className="spinner-border spinner-border-sm me-2" />}
          Save & start applying <i className="bi bi-arrow-right" />
        </button>
      </>
    ) : null;

  return (
    <Modal show={addJob.open} onClose={closeAddJob} title="Add a job" footer={footer} size="modal-lg">
      {step === "paste" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            detect(url);
          }}
        >
          <label className="form-label fw-medium" htmlFor="job-url">
            Paste the job link
          </label>
          <div className="input-group input-group-lg mb-2">
            <span className="input-group-text bg-white">
              <i className="bi bi-link-45deg" />
            </span>
            <input
              id="job-url"
              className="form-control"
              placeholder="https://boards.greenhouse.io/company/jobs/123"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
          </div>
          <p className="text-muted small mb-3">
            Works best with Greenhouse, Lever, Ashby, Workday and most company career pages. We'll pull out the title,
            company, location and requirements for you.
          </p>
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          <div className="d-flex flex-column flex-sm-row gap-2">
            <button type="submit" className="btn btn-primary" disabled={!url.trim()}>
              <i className="bi bi-magic me-1" /> Detect job details
            </button>
            {integrations.ai && (
              <button type="button" className="btn btn-light" onClick={() => setStep("text")}>
                Paste the description instead
              </button>
            )}
            <button type="button" className="btn btn-link text-muted" onClick={enterManually}>
              Enter details manually
            </button>
          </div>
        </form>
      )}

      {step === "text" && (
        <div>
          {error && <div className="alert alert-warning py-2 small">{error}</div>}
          <label className="form-label fw-medium" htmlFor="job-text">
            Paste the job description
          </label>
          <textarea
            id="job-text"
            className="form-control mb-2"
            rows={9}
            placeholder="Copy everything from the job page (title, company, responsibilities, requirements) and paste it here."
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            autoFocus
          />
          <label className="form-label small fw-medium" htmlFor="job-text-url">
            Job link <span className="text-muted fw-normal">(optional)</span>
          </label>
          <input
            id="job-text-url"
            className="form-control mb-3"
            placeholder="https://"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <div className="d-flex flex-column flex-sm-row gap-2">
            <button className="btn btn-primary" onClick={detectFromText} disabled={pasted.trim().length < 50}>
              <i className="bi bi-magic me-1" /> Pull out the details
            </button>
            <button className="btn btn-light" onClick={() => setStep("paste")}>
              Back
            </button>
          </div>
        </div>
      )}

      {step === "detecting" && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status" />
          <div className="fw-medium mb-3">Detecting job details…</div>
          <ul className="list-unstyled small text-muted d-inline-block text-start mb-0">
            {DETECT_STEPS.map((label, i) => (
              <li key={label} className={i <= detectIndex ? "text-body" : ""}>
                <i className={`bi me-2 ${i < detectIndex ? "bi-check-circle-fill text-success" : "bi-circle"}`} />
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === "review" && (
        <div>
          {notice && (
            <div className={`alert alert-${notice.variant} py-2 small d-flex align-items-center gap-2`}>
              <i className={`bi ${notice.variant === "success" ? "bi-check-circle-fill" : "bi-info-circle-fill"}`} />
              {notice.text}
            </div>
          )}
          {duplicate && (
            <div className="alert alert-warning py-2 small">
              You're already tracking this job.{" "}
              <Link to={`/jobs/${duplicate.id}`} onClick={closeAddJob}>
                Open it
              </Link>
            </div>
          )}
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-medium">Job title *</label>
              <input className="form-control" value={form.title} onChange={set("title")} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-medium">Company *</label>
              <input className="form-control" value={form.company} onChange={set("company")} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-medium">Location</label>
              <input className="form-control" value={form.location} onChange={set("location")} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-medium">Work type</label>
              <select className="form-select" value={form.workType} onChange={set("workType")}>
                <option>Remote</option>
                <option>Hybrid</option>
                <option>On-site</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-medium">Salary</label>
              <input className="form-control" value={form.salary} onChange={set("salary")} placeholder="Optional" />
            </div>
            <div className="col-12">
              <label className="form-label small fw-medium">Job link</label>
              <input className="form-control" value={form.url} onChange={set("url")} placeholder="https://" />
            </div>
            <div className="col-12">
              <label className="form-label small fw-medium">Key requirements</label>
              <input
                className="form-control"
                value={form.requirements.join(", ")}
                onChange={(e) =>
                  setForm((f) => ({ ...f, requirements: e.target.value.split(",").map((s) => s.trimStart()) }))
                }
                placeholder="React, TypeScript, …"
              />
              <div className="form-text">Separate with commas. Used to tailor your resume and cover letter.</div>
            </div>
            <div className="col-12">
              <label className="form-label small fw-medium">Description</label>
              <textarea className="form-control" rows={6} value={form.description} onChange={set("description")} />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default AddJobModal;
